import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { Select as RSelect } from 'chakra-react-select';
import {
  ArrowRight,
  ArrowsLeftRight,
  Bank,
  CaretDown,
  Check,
  Copy,
  FilePdf,
  LockSimple,
  Receipt,
  WarningCircle,
} from 'phosphor-react';

import {
  IContaCilLancamento,
  IContaCilNotaSugerida,
  useContaCilLancamentoExtratoSaldo,
  useContaCilUpdateLancamento,
} from '../../../../../../hooks/useContaCilLancamentos';
import {
  IExtratoItem,
  baixarExtrato,
  useContaCilExtratoDetalhe,
  useExcluirExtrato,
  useReprocessarExtrato,
} from '../../../../../../hooks/useContaCilExtratos';
import {
  useContaCilLancamentoDebug,
  useContaCilLancamentoTimeline,
  useContaCilRagDebug,
} from '../../../../../../hooks/useContaCilSchoolAi';
import { useAuth } from '../../../../../../providers/auth';
import { extractApiErrorMessage } from '../../../../../../utils/ErrorUtils';
import { ExtratoDetailModal } from '../../extratos/components/ExtratoDetailModal';
import { ExtratoPdfModal } from '../../extratos/components/ExtratoPdfModal';
import {
  PocSelectGroup,
  PocSelectOption,
  PocSelectTooltip,
  POC_COMPONENTS_C,
  POC_COMPONENTS_D,
  STATUS_BADGE,
  corConfianca,
  fmtData,
  fmtDoc,
  fmtValor,
  pocSelectStyles,
} from '../shared';
import { propsMenuSelect } from '../../../../../../utils/selectMenu';

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box minW={0}>
      <Text
        fontSize="10px"
        fontWeight="600"
        color="#94A3B8"
        textTransform="uppercase"
        letterSpacing="0.06em"
        mb={0.5}
      >
        {label}
      </Text>
      <Box fontSize="sm" color="#0F172A" fontWeight="600">
        {children}
      </Box>
    </Box>
  );
}

// Sprint 5 — Diagnostico + Timeline. So aparece pra admin/dev e usa
// os endpoints novos /debug e /timeline (consomem Sprint 0 events).
function SchoolAiDebugSection({ lancamentoId }: { lancamentoId: number }) {
  const { isTechnical } = useAuth();
  const [aba, setAba] = useState<'diag' | 'timeline' | 'exemplos'>('diag');
  const dbg = useContaCilLancamentoDebug(isTechnical() ? lancamentoId : null);
  const tl = useContaCilLancamentoTimeline(
    isTechnical() && aba === 'timeline' ? lancamentoId : null,
  );
  const rag = useContaCilRagDebug(isTechnical() && aba === 'exemplos' ? lancamentoId : null);

  if (!isTechnical()) return null;

  const corPasso: Record<string, string> = {
    bateu: '#10B981',
    nao_bateu: '#94A3B8',
    nao_tentou: '#CBD5E1',
    em_progresso: '#F59E0B',
  };
  const iconPasso: Record<string, string> = {
    bateu: '✓',
    nao_bateu: '✕',
    nao_tentou: '○',
    em_progresso: '…',
  };
  const fonteLabel: Record<string, string> = {
    regra_hardcoded: 'Regra hardcoded (142 builtins)',
    regra_custom: 'Regra customizada',
    memoria: 'Memória L2',
    ia: 'IA (Claude)',
    manual: 'Edição manual',
  };

  return (
    <Box mt={5} pt={4} borderTopWidth="1px" borderColor="rgba(15,23,42,0.06)">
      <Flex justify="space-between" align="center" mb={2}>
        <Flex gap={2}>
          <Button
            size="xs"
            variant={aba === 'diag' ? 'solid' : 'ghost'}
            colorScheme="purple"
            onClick={() => setAba('diag')}
          >
            Diagnóstico
          </Button>
          <Button
            size="xs"
            variant={aba === 'timeline' ? 'solid' : 'ghost'}
            colorScheme="purple"
            onClick={() => setAba('timeline')}
          >
            Linha do tempo
          </Button>
          <Button
            size="xs"
            variant={aba === 'exemplos' ? 'solid' : 'ghost'}
            colorScheme="purple"
            onClick={() => setAba('exemplos')}
          >
            Exemplos (RAG)
          </Button>
        </Flex>
        <Text fontSize="9px" color="#94A3B8" textTransform="uppercase" letterSpacing="0.08em">
          School AI
        </Text>
      </Flex>

      {aba === 'diag' && (
        <Box>
          {dbg.isLoading && (
            <Text fontSize="11px" color="#64748B">
              Diagnosticando…
            </Text>
          )}
          {dbg.data && (
            <>
              {dbg.data.perna_faltando && (
                <Flex
                  align="center"
                  gap={2}
                  bg="rgba(239,68,68,0.08)"
                  color="#991B1B"
                  borderRadius="md"
                  p={2}
                  mb={2}
                  fontSize="11px"
                >
                  <WarningCircle size={14} weight="fill" />
                  <Text>
                    Perna que falta: <b>{dbg.data.perna_faltando}</b>
                  </Text>
                </Flex>
              )}
              <Flex direction="column" gap={1.5}>
                {dbg.data.passos.map(p => (
                  <Flex key={p.fonte} gap={2} align="flex-start" fontSize="11px">
                    <Box
                      w="20px"
                      h="20px"
                      borderRadius="full"
                      bg={`${corPasso[p.status] ?? '#94A3B8'}22`}
                      color={corPasso[p.status] ?? '#94A3B8'}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontWeight="700"
                      flexShrink={0}
                    >
                      {iconPasso[p.status] ?? '?'}
                    </Box>
                    <Box flex={1} minW={0}>
                      <Text fontWeight="600" color="#0F172A">
                        {fonteLabel[p.fonte] ?? p.fonte}
                      </Text>
                      <Text color="#64748B">{p.detalhe}</Text>
                    </Box>
                  </Flex>
                ))}
              </Flex>
              {dbg.data.sugestoes_ia_top3 && dbg.data.sugestoes_ia_top3.length > 0 && (
                <Box mt={3} pt={2} borderTopWidth="1px" borderColor="rgba(15,23,42,0.04)">
                  <Text fontSize="10px" color="#64748B" mb={1} fontWeight="600">
                    Top-3 alternativas da IA
                  </Text>
                  <Flex direction="column" gap={1}>
                    {dbg.data.sugestoes_ia_top3.slice(0, 3).map(s => (
                      <Flex
                        key={`${s.cod ?? '?'}-${s.nm ?? ''}`}
                        justify="space-between"
                        fontSize="11px"
                      >
                        <Text color="#0F172A" noOfLines={1} flex={1} minW={0}>
                          {s.cod ?? '—'} · {s.nm ?? ''}
                        </Text>
                        <Text color="#64748B" flexShrink={0} ml={2}>
                          {s.score != null ? `${(s.score * 100).toFixed(0)}%` : '—'}
                        </Text>
                      </Flex>
                    ))}
                  </Flex>
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {aba === 'timeline' && (
        <Box>
          {tl.isLoading && (
            <Text fontSize="11px" color="#64748B">
              Carregando timeline…
            </Text>
          )}
          {tl.data && tl.data.eventos.length === 0 && (
            <Text fontSize="11px" color="#94A3B8" fontStyle="italic">
              Sem eventos registrados — Sprint 0 começou a capturar a partir da migration.
            </Text>
          )}
          {tl.data && tl.data.eventos.length > 0 && (
            <Flex direction="column" gap={2}>
              {tl.data.eventos.map(ev => {
                const dt = new Date(ev.dt_evento).toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                });
                return (
                  <Box
                    key={ev.id}
                    p={2}
                    borderRadius="md"
                    bg="rgba(255,255,255,0.55)"
                    borderWidth="1px"
                    borderColor="rgba(15,23,42,0.06)"
                    fontSize="11px"
                  >
                    <Flex justify="space-between" align="center" mb={0.5}>
                      <Flex align="center" gap={1.5}>
                        <Badge colorScheme="purple" fontSize="9px">
                          {ev.evento_tipo}
                        </Badge>
                        {ev.fonte && <Text color="#64748B">{ev.fonte}</Text>}
                      </Flex>
                      <Text color="#94A3B8" fontSize="10px">
                        {dt}
                      </Text>
                    </Flex>
                    {ev.campos_alterados && ev.campos_alterados.length > 0 && (
                      <Text color="#64748B">
                        Mudou: <b>{ev.campos_alterados.join(', ')}</b>
                      </Text>
                    )}
                    {ev.motivo && (
                      <Text color="#64748B" fontStyle="italic">
                        &ldquo;{ev.motivo}&rdquo;
                      </Text>
                    )}
                  </Box>
                );
              })}
            </Flex>
          )}
        </Box>
      )}

      {aba === 'exemplos' && (
        <Box>
          {rag.isLoading && (
            <Text fontSize="11px" color="#64748B">
              Buscando exemplos no livro-razão…
            </Text>
          )}
          {rag.data?.aviso && (
            <Text fontSize="11px" color="#F59E0B" fontStyle="italic">
              {rag.data.aviso}
            </Text>
          )}
          {rag.data && rag.data.exemplos.length === 0 && !rag.data.aviso && (
            <Text fontSize="11px" color="#94A3B8" fontStyle="italic">
              Nenhum exemplo similar no livro-razão (com corte temporal 90d + filtro anti-feedback).
            </Text>
          )}
          {rag.data && rag.data.exemplos.length > 0 && (
            <>
              <Text fontSize="10px" color="#64748B" mb={2}>
                A IA receberia {rag.data.exemplos.length} exemplo(s) como few-shot. [HUMANO] =
                confirmado por contador · [IA] = excluído por padrão.
              </Text>
              <Flex direction="column" gap={1.5}>
                {rag.data.exemplos.map(ex => (
                  <Box
                    key={ex.id}
                    p={2}
                    borderRadius="md"
                    bg={ex.origem_humano ? 'rgba(16,185,129,0.06)' : 'rgba(245,158,11,0.06)'}
                    borderWidth="1px"
                    borderColor={
                      ex.origem_humano ? 'rgba(16,185,129,0.18)' : 'rgba(245,158,11,0.18)'
                    }
                    fontSize="11px"
                  >
                    <Flex justify="space-between" align="flex-start" gap={2}>
                      <Box flex={1} minW={0}>
                        <Flex align="center" gap={1.5} mb={0.5}>
                          <Badge colorScheme={ex.origem_humano ? 'green' : 'orange'} fontSize="9px">
                            {ex.origem_humano ? 'HUMANO' : 'IA'}
                          </Badge>
                          <Text color="#94A3B8" fontSize="9px">
                            sim {(ex.score * 100).toFixed(0)}%
                            {ex.data && ` · ${new Date(ex.data).toLocaleDateString('pt-BR')}`}
                          </Text>
                        </Flex>
                        <Text color="#0F172A" noOfLines={2}>
                          {ex.historico}
                        </Text>
                        <Text color="#64748B" mt={0.5}>
                          D=<b>{ex.conta_debito ?? '—'}</b> ({ex.conta_debito_nome ?? '—'}){' · '}
                          C=<b>{ex.conta_credito ?? '—'}</b> ({ex.conta_credito_nome ?? '—'})
                        </Text>
                      </Box>
                      {ex.valor != null && (
                        <Text fontWeight="600" color="#0F172A" flexShrink={0}>
                          R$ {ex.valor.toFixed(2)}
                        </Text>
                      )}
                    </Flex>
                  </Box>
                ))}
              </Flex>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

interface Props {
  lanc: IContaCilLancamento | null;
  pocOptions: PocSelectOption[];
  pocGroups: PocSelectGroup[];
  nota?: IContaCilNotaSugerida;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  empresaId?: number | null;
}

export function LancamentoDetailModal({
  lanc,
  pocOptions,
  pocGroups,
  nota,
  isOpen,
  onClose,
  onSaved,
  empresaId,
}: Props) {
  const toast = useToast();
  const { mutateAsync, isPending } = useContaCilUpdateLancamento();
  const [pdfOpen, setPdfOpen] = useState(false);
  const { data: extratoSaldo } = useContaCilLancamentoExtratoSaldo(lanc?.id, isOpen);
  // Cor do "mês anterior fechou em": verde=contínuo, laranja=quebra, cinza=
  // indeterminado por gap. if/else evita ternário aninhado (eslint airbnb).
  let corContinuidade = '#64748B';
  if (extratoSaldo?.continuo === true) corContinuidade = '#10B981';
  else if (extratoSaldo?.continuo === false) corContinuidade = '#F59E0B';

  // "Ver extrato": abre o ExtratoDetailModal do extrato de origem (IExtratoDetail
  // estende IExtratoItem, então o detalhe serve direto de prop).
  const [verExtratoOpen, setVerExtratoOpen] = useState(false);
  const reprocessarExtrato = useReprocessarExtrato();
  const excluirExtrato = useExcluirExtrato();
  const { data: extratoDetalhe } = useContaCilExtratoDetalhe(
    extratoSaldo?.extrato_id ?? undefined,
    verExtratoOpen,
  );

  const onBaixarExtrato = async (item: IExtratoItem) => {
    try {
      const { url } = await baixarExtrato(item.id);
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast({ status: 'error', title: extractApiErrorMessage(err, 'Falha ao baixar.') });
    }
  };
  const onReprocessarExtrato = async (item: IExtratoItem) => {
    try {
      await reprocessarExtrato.mutateAsync(item.id);
      toast({ status: 'success', title: 'Reprocessamento enfileirado.' });
    } catch (err) {
      toast({ status: 'error', title: extractApiErrorMessage(err, 'Falha ao reprocessar.') });
    }
  };
  const onExcluirExtrato = (item: IExtratoItem) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm('Excluir este extrato e seus lançamentos?');
    if (!ok) return;
    excluirExtrato.mutate(item.id, {
      onSuccess: () => {
        // O lançamento atual foi cascade-deletado junto -> fecha os dois modais.
        setVerExtratoOpen(false);
        onClose();
      },
    });
  };

  const isEntrada = lanc?.dc === 'C';
  const contraField: 'debito_poc' | 'credito_poc' = isEntrada ? 'credito_poc' : 'debito_poc';
  const contraPocCod = isEntrada ? lanc?.credito_poc : lanc?.debito_poc;
  const contraValue = pocOptions.find(o => o.value === contraPocCod) ?? null;
  // Perna-banco (a outra metade da partida). Vem da POC da conta-banco; se a
  // conta não está vinculada a uma POC no plano, fica vazia -> o lançamento
  // segue pendente mesmo com a contrapartida classificada.
  const bankPocCod = isEntrada ? lanc?.debito_poc : lanc?.credito_poc;
  const faltaContaBanco = !!lanc && lanc.status !== 'conciliado' && !bankPocCod;
  const podeConciliar = !!contraPocCod;
  const jaConciliado = lanc?.status === 'conciliado';
  const corValor = isEntrada ? '#10B981' : '#EF4444';
  const badge = lanc ? (STATUS_BADGE[lanc.status] ?? { cor: 'gray', label: lanc.status }) : null;
  const pctConf = lanc?.confianca != null ? Math.round(lanc.confianca * 100) : null;

  const salvar = async (valor: string | null) => {
    if (!lanc) return;
    try {
      await mutateAsync({ id: lanc.id, [contraField]: valor });
      onSaved();
    } catch (err) {
      toast({
        status: 'error',
        title: 'Falha ao vincular contrapartida',
        description: extractApiErrorMessage(err, 'Tente novamente.'),
      });
    }
  };

  const concluir = async () => {
    if (!lanc || !contraPocCod) return;
    if (jaConciliado) {
      onClose();
      return;
    }
    try {
      await mutateAsync({ id: lanc.id, [contraField]: contraPocCod });
      onSaved();
      onClose();
    } catch (err) {
      toast({
        status: 'error',
        title: 'Falha ao conciliar',
        description: extractApiErrorMessage(err, 'Tente novamente.'),
      });
    }
  };

  const copiar = (txt: string, label: string) => {
    navigator.clipboard?.writeText(txt).then(
      () => toast({ status: 'success', title: `${label} copiado`, duration: 1500 }),
      () => undefined,
    );
  };

  // Caixa de um lado da partida (banco automático). Sem POC da conta-banco no
  // plano, a perna fica vazia -> sinaliza "sem POC" (âmbar) em vez de "automático".
  const ladoBanco = (titulo: string) => (
    <Box
      borderWidth="1px"
      borderColor={faltaContaBanco ? 'rgba(245, 158, 11, 0.45)' : 'rgba(15, 23, 42, 0.08)'}
      borderRadius="xl"
      p={3}
      bg={faltaContaBanco ? 'rgba(245, 158, 11, 0.06)' : 'rgba(255, 255, 255, 0.5)'}
      minW={0}
    >
      <Flex align="center" justify="space-between" mb={2}>
        <Text
          fontSize="10px"
          fontWeight="700"
          letterSpacing="0.06em"
          color={titulo === 'DÉBITO' ? '#059669' : '#DC2626'}
        >
          {titulo}
        </Text>
        {faltaContaBanco ? (
          <Flex align="center" gap={1} color="#B45309">
            <WarningCircle size={11} weight="bold" />
            <Text fontSize="10px" fontWeight="600">
              sem POC
            </Text>
          </Flex>
        ) : (
          <Flex align="center" gap={1} color="#94A3B8">
            <LockSimple size={11} weight="bold" />
            <Text fontSize="10px" fontWeight="600">
              automático
            </Text>
          </Flex>
        )}
      </Flex>
      <Text fontSize="sm" color="#0F172A" fontWeight="600" isTruncated>
        {lanc?.banco_nome ?? 'Banco'}
        {lanc?.conta ? ` · ${lanc.conta}` : ''}
      </Text>
      <Text fontSize="10px" color={faltaContaBanco ? '#B45309' : '#94A3B8'} mb={1}>
        {faltaContaBanco ? 'conta-banco sem POC no plano' : 'conta do banco'}
      </Text>
      <Text fontSize="sm" fontWeight="700" color="#475569">
        {fmtValor(lanc?.valor)}
      </Text>
    </Box>
  );

  // Caixa de um lado da partida (contrapartida editável).
  const ladoContra = (titulo: string) => (
    <Box
      borderWidth="1px"
      borderColor="rgba(99, 102, 241, 0.35)"
      borderRadius="xl"
      p={3}
      bg="rgba(99, 102, 241, 0.04)"
      minW={0}
    >
      <Flex align="center" justify="space-between" mb={2}>
        <Text
          fontSize="10px"
          fontWeight="700"
          letterSpacing="0.06em"
          color={titulo === 'DÉBITO' ? '#059669' : '#DC2626'}
        >
          {titulo}
        </Text>
        <Text fontSize="10px" fontWeight="600" color="#6366F1">
          você escolhe
        </Text>
      </Flex>
      <PocSelectTooltip label={contraValue?.label}>
        <RSelect<PocSelectOption>
          size="sm"
          placeholder="Escolher contrapartida..."
          options={pocGroups}
          value={contraValue}
          onChange={opt => salvar(opt ? opt.value : null)}
          isClearable
          isDisabled={isPending || !!lanc?.extrato_bloqueado}
          components={isEntrada ? POC_COMPONENTS_C : POC_COMPONENTS_D}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          {...propsMenuSelect}
          styles={pocSelectStyles}
          chakraStyles={{ control: p => ({ ...p, borderRadius: 'lg', minHeight: '36px' }) }}
        />
      </PocSelectTooltip>
      <Text fontSize="10px" color="#94A3B8" mt={1}>
        contrapartida (POC)
      </Text>
    </Box>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={{ base: 'full', md: '3xl' }}
        isCentered
        scrollBehavior="inside"
        motionPreset="slideInBottom"
        // Solta a trava de scroll enquanto "Ver extrato" está aberto por cima.
        //
        // O Chakra só liga o RemoveScroll do modal que está na BASE da pilha
        // (`modal-focus.mjs`: `enabled: index === 1 && blockScrollOnMount`, com
        // o índice vindo do ModalManager). Com dois modais abertos, a única
        // trava ativa é a DESTE, e o `lockRef` dela aponta pro conteúdo daqui —
        // então a roda do mouse sobre o modal de cima cai no ramo "evento de
        // fora" do react-remove-scroll e leva `preventDefault()`. O de cima
        // ficava impossível de rolar (só a barra funcionava, porque arrastar a
        // barra não gera evento de wheel).
        //
        // Desligar aqui deixa o de cima rolar. O custo é o fundo poder rolar
        // junto enquanto ele está aberto — bem menor que não rolar nada.
        blockScrollOnMount={!verExtratoOpen}
      >
        <ModalOverlay
          bg="rgba(15, 23, 42, 0.45)"
          backdropFilter="blur(8px)"
          sx={{ WebkitBackdropFilter: 'blur(8px)' }}
        />
        <ModalContent
          bg="rgba(255, 255, 255, 0.82)"
          backdropFilter="blur(40px) saturate(200%)"
          sx={{ WebkitBackdropFilter: 'blur(40px) saturate(200%)' }}
          borderWidth="1px"
          borderColor="rgba(255, 255, 255, 0.6)"
          borderRadius={{ base: 'none', md: '3xl' }}
          boxShadow="0 1px 0 0 rgba(255,255,255,0.9) inset, 0 24px 70px -18px rgba(27, 136, 245, 0.30), 0 8px 24px -8px rgba(15, 23, 42, 0.12)"
          maxH={{ base: '100dvh', md: '88vh' }}
          overflow="hidden"
        >
          <ModalHeader pt={5} pb={2}>
            <Flex align="center" gap={3}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="44px"
                h="44px"
                borderRadius="xl"
                flexShrink={0}
                bg={
                  isEntrada
                    ? 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)'
                    : 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)'
                }
                color={isEntrada ? '#059669' : '#DC2626'}
              >
                <ArrowsLeftRight size={24} weight="duotone" />
              </Box>
              <Box minW={0} flex={1}>
                <Flex align="center" gap={2} wrap="wrap">
                  <Heading
                    fontSize="lg"
                    fontWeight="700"
                    color="#0F172A"
                    lineHeight="1.2"
                    isTruncated
                    maxW="300px"
                  >
                    {lanc?.contraparte || lanc?.historico || 'Lançamento'}
                  </Heading>
                  {badge && (
                    <Badge colorScheme={badge.cor} borderRadius="full" px={2}>
                      {badge.label}
                    </Badge>
                  )}
                  {lanc?.fonte_match === 'ia' && (
                    <Badge
                      borderRadius="full"
                      px={2}
                      fontSize="8px"
                      color="white"
                      bgGradient="linear(135deg, #6366F1, #EC4899)"
                    >
                      IA
                    </Badge>
                  )}
                  {lanc?.tipo_operacao && (
                    <Badge colorScheme="gray" borderRadius="full" px={2}>
                      {lanc.tipo_operacao.toUpperCase()}
                    </Badge>
                  )}
                </Flex>
                <Text fontSize="xs" color="#64748B" isTruncated>
                  {[lanc?.banco_nome, lanc?.conta, lanc ? fmtData(lanc.data) : null]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              </Box>
              <Box textAlign="right" flexShrink={0} pr={8}>
                <Text fontSize="2xl" fontWeight="800" color={corValor} lineHeight="1">
                  {isEntrada ? '+' : '−'} {fmtValor(lanc?.valor)}
                </Text>
                <Text fontSize="10px" color="#94A3B8">
                  {isEntrada ? 'Entrada' : 'Saída'}
                </Text>
              </Box>
            </Flex>
          </ModalHeader>
          <ModalCloseButton top={4} color="#64748B" />

          <ModalBody pb={4} pt={2}>
            {lanc && (
              <>
                {/* Aviso automático (Fase 2) */}
                {lanc.auto_aviso && (
                  <Flex
                    align="flex-start"
                    gap={2}
                    bg="rgba(245, 158, 11, 0.12)"
                    color="#B45309"
                    borderRadius="xl"
                    p={3}
                    mb={4}
                    fontSize="13px"
                  >
                    <Box mt="2px" flexShrink={0}>
                      <WarningCircle size={16} weight="fill" />
                    </Box>
                    <Text>{lanc.auto_aviso}</Text>
                  </Flex>
                )}
                {/* Transferência interna (Fase 2) */}
                {lanc.cross_link && (
                  <Flex
                    align="center"
                    gap={2}
                    bg="rgba(99, 102, 241, 0.08)"
                    color="#4338CA"
                    borderRadius="xl"
                    p={3}
                    mb={4}
                    fontSize="13px"
                  >
                    <Box flexShrink={0}>
                      <ArrowsLeftRight size={16} weight="bold" />
                    </Box>
                    <Text>
                      Transferência interna entre contas da própria empresa — não é receita nem
                      despesa.
                    </Text>
                  </Flex>
                )}

                {/* Histórico cru */}
                <Flex align="center" justify="space-between" mb={1}>
                  <Text
                    fontSize="10px"
                    fontWeight="600"
                    color="#94A3B8"
                    textTransform="uppercase"
                    letterSpacing="0.06em"
                  >
                    Histórico
                  </Text>
                  <IconButton
                    aria-label="Copiar histórico"
                    icon={<Copy size={13} weight="bold" />}
                    size="xs"
                    variant="ghost"
                    color="#94A3B8"
                    onClick={() => copiar(lanc.historico, 'Histórico')}
                  />
                </Flex>
                <Box
                  bg="rgba(15, 23, 42, 0.03)"
                  borderRadius="lg"
                  p={3}
                  mb={5}
                  fontSize="12px"
                  color="#0F172A"
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                >
                  {lanc.historico}
                </Box>

                {/* Partida dobrada */}
                <Text
                  fontSize="10px"
                  fontWeight="600"
                  color="#94A3B8"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                  mb={2}
                >
                  Partida dobrada
                </Text>
                <Flex align="stretch" gap={3} direction={{ base: 'column', md: 'row' }} mb={1}>
                  <Box flex={1} minW={0}>
                    {isEntrada ? ladoBanco('DÉBITO') : ladoContra('DÉBITO')}
                  </Box>
                  <Flex align="center" justify="center" color="#CBD5E1" flexShrink={0}>
                    <Box display={{ base: 'none', md: 'block' }}>
                      <ArrowRight size={18} weight="bold" />
                    </Box>
                    <Box display={{ base: 'block', md: 'none' }}>
                      <CaretDown size={18} weight="bold" />
                    </Box>
                  </Flex>
                  <Box flex={1} minW={0}>
                    {isEntrada ? ladoContra('CRÉDITO') : ladoBanco('CRÉDITO')}
                  </Box>
                </Flex>
                <Text fontSize="11px" color="#64748B" mb={faltaContaBanco ? 2 : 5}>
                  {isEntrada
                    ? 'entrada credita a contrapartida; o débito entra no banco'
                    : 'saída debita a contrapartida; o crédito sai do banco'}
                </Text>
                {faltaContaBanco && (
                  <Flex
                    align="flex-start"
                    gap={2}
                    bg="rgba(245, 158, 11, 0.08)"
                    borderWidth="1px"
                    borderColor="rgba(245, 158, 11, 0.35)"
                    borderRadius="xl"
                    p={3}
                    mb={5}
                  >
                    <Box color="#B45309" mt="1px" flexShrink={0}>
                      <WarningCircle size={16} weight="fill" />
                    </Box>
                    <Box>
                      <Text fontSize="12px" color="#92400E">
                        A conta-banco{' '}
                        <Text as="span" fontWeight="700">
                          {lanc?.banco_nome ?? 'banco'}
                          {lanc?.conta ? ` · ${lanc.conta}` : ''}
                        </Text>{' '}
                        não está vinculada a uma POC no plano de contas — por isso fica pendente
                        mesmo com a contrapartida classificada.
                      </Text>
                      {empresaId != null && (
                        <Button
                          size="xs"
                          mt={2}
                          leftIcon={<Bank size={13} weight="bold" />}
                          colorScheme="orange"
                          variant="solid"
                          borderRadius="lg"
                          onClick={() =>
                            window.open(
                              `/modulesHome/accountingConciliation/conciliacao-contabil/bancos?empresa=${empresaId}`,
                              '_blank',
                              'noopener',
                            )
                          }
                        >
                          Vincular conta no Bancos
                        </Button>
                      )}
                    </Box>
                  </Flex>
                )}

                {/* Sugestões da IA (Fase 2) — aceitar em 1 clique */}
                {lanc.ia_alternativas && lanc.ia_alternativas.length > 0 && (
                  <Box mb={5}>
                    <Text
                      fontSize="10px"
                      fontWeight="600"
                      color="#94A3B8"
                      textTransform="uppercase"
                      letterSpacing="0.06em"
                      mb={2}
                    >
                      Sugestões da IA
                    </Text>
                    <Flex direction="column" gap={2}>
                      {lanc.ia_alternativas.slice(0, 3).map((alt, i) => {
                        const score = alt.score != null ? Math.round(alt.score * 100) : null;
                        return (
                          <Flex
                            key={alt.cod ?? i}
                            align="center"
                            gap={3}
                            borderWidth="1px"
                            borderColor={
                              i === 0 ? 'rgba(99, 102, 241, 0.35)' : 'rgba(15, 23, 42, 0.06)'
                            }
                            borderRadius="xl"
                            p={2.5}
                            pl={3}
                          >
                            <Box minW={0} flex={1}>
                              <Text fontSize="13px" fontWeight="600" color="#0F172A" isTruncated>
                                {alt.cod} · {alt.nm}
                              </Text>
                              {score != null && (
                                <Flex align="center" gap={2} mt={1}>
                                  <Box
                                    h="4px"
                                    w="80px"
                                    borderRadius="full"
                                    bg="rgba(99, 102, 241, 0.12)"
                                    overflow="hidden"
                                  >
                                    <Box
                                      h="full"
                                      w={`${score}%`}
                                      borderRadius="full"
                                      bgGradient="linear(135deg, #6366F1, #EC4899)"
                                    />
                                  </Box>
                                  <Text fontSize="10px" color="#94A3B8" fontWeight="600">
                                    {score}%
                                  </Text>
                                </Flex>
                              )}
                            </Box>
                            <Button
                              size="xs"
                              variant={i === 0 ? 'solid' : 'outline'}
                              colorScheme="purple"
                              borderRadius="lg"
                              isLoading={isPending}
                              isDisabled={!alt.cod || !!lanc?.extrato_bloqueado}
                              onClick={() => alt.cod && salvar(alt.cod)}
                            >
                              Usar
                            </Button>
                          </Flex>
                        );
                      })}
                    </Flex>
                  </Box>
                )}

                {/* Detalhes */}
                <Text
                  fontSize="10px"
                  fontWeight="600"
                  color="#94A3B8"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                  mb={3}
                >
                  Detalhes
                </Text>
                <SimpleGrid
                  columns={{ base: 2, md: 3 }}
                  spacingX={4}
                  spacingY={4}
                  mb={nota ? 5 : 0}
                >
                  <Campo label="Data">{fmtData(lanc.data)}</Campo>
                  <Campo label="Documento">{lanc.documento || '—'}</Campo>
                  <Campo label="Valor">
                    <Text color={corValor}>
                      {isEntrada ? '+' : '−'} {fmtValor(lanc.valor)}
                    </Text>
                  </Campo>
                  <Campo label="Saldo após">{fmtValor(lanc.saldo)}</Campo>
                  <Campo label="Banco / Conta">
                    {lanc.banco_nome ?? '—'}
                    {lanc.conta ? ` · ${lanc.conta}` : ''}
                  </Campo>
                  <Campo label="Categoria">{lanc.categoria || '—'}</Campo>
                  <Campo label="Tipo operação">{lanc.tipo_operacao?.toUpperCase() || '—'}</Campo>
                  <Campo label="Contraparte">
                    <Tooltip
                      label={lanc.contraparte ?? ''}
                      hasArrow
                      openDelay={300}
                      isDisabled={!lanc.contraparte}
                    >
                      <Text isTruncated>{lanc.contraparte || '—'}</Text>
                    </Tooltip>
                  </Campo>
                  <Campo label="CNPJ / CPF">
                    {lanc.contraparte_cnpj_cpf ? (
                      <Flex align="center" gap={1}>
                        <Text color="#1B88F5">{fmtDoc(lanc.contraparte_cnpj_cpf)}</Text>
                        <IconButton
                          aria-label="Copiar documento"
                          icon={<Copy size={12} weight="bold" />}
                          size="xs"
                          variant="ghost"
                          color="#94A3B8"
                          minW="auto"
                          h="auto"
                          p={0.5}
                          onClick={() => copiar(lanc.contraparte_cnpj_cpf as string, 'Documento')}
                        />
                      </Flex>
                    ) : (
                      '—'
                    )}
                  </Campo>
                  <Campo label="Confiança">
                    {pctConf != null ? (
                      <Flex align="center" gap={1}>
                        <Box w="8px" h="8px" borderRadius="full" bg={corConfianca(pctConf)} />
                        <Text>{pctConf}%</Text>
                      </Flex>
                    ) : (
                      '—'
                    )}
                  </Campo>
                  <Campo label="Fonte do match">{lanc.fonte_match || '—'}</Campo>
                  <Campo label="Auto-conciliado">
                    <Text color={lanc.auto_conciliado ? '#059669' : '#94A3B8'}>
                      {lanc.auto_conciliado ? 'sim' : 'não'}
                    </Text>
                  </Campo>
                </SimpleGrid>

                {/* NF candidata (quando o pai passa a sugestão) */}
                {nota && (
                  <Box
                    borderWidth="1px"
                    borderColor="rgba(15, 23, 42, 0.06)"
                    borderRadius="xl"
                    p={3}
                    bg="rgba(255, 255, 255, 0.5)"
                  >
                    <Flex align="center" gap={2} mb={1} wrap="wrap">
                      <Text fontSize="sm" fontWeight="700" color="#0F172A">
                        Nota fiscal candidata
                      </Text>
                      {nota.match_cnpj ? (
                        <Badge colorScheme="green" variant="solid" fontSize="9px">
                          CNPJ ✓
                        </Badge>
                      ) : (
                        nota.match_nome && (
                          <Badge colorScheme="green" variant="subtle" fontSize="9px">
                            nome ✓
                          </Badge>
                        )
                      )}
                    </Flex>
                    <Text fontSize="13px" color="#475569">
                      {nota.tipo === 'entrada' ? 'NF entrada' : 'NF saída'} {nota.numero_nf}
                      {nota.serie ? ` · série ${nota.serie}` : ''}
                      {nota.data ? ` · ${fmtData(nota.data)}` : ''} · {fmtValor(nota.valor)}
                    </Text>
                    {nota.contraparte && (
                      <Text fontSize="11px" color="#94A3B8" isTruncated>
                        {nota.contraparte}
                        {nota.candidatos > 1 ? ` · +${nota.candidatos - 1} candidata(s)` : ''}
                      </Text>
                    )}
                  </Box>
                )}

                {/* Sprint 5 — Diagnostico + Timeline (admin/dev) */}
                <SchoolAiDebugSection lancamentoId={lanc.id} />
              </>
            )}

            {extratoSaldo?.encontrado && (
              <Box mt={5} pt={4} borderTopWidth="1px" borderColor="rgba(15, 23, 42, 0.06)">
                <Flex align="center" justify="space-between" mb={2}>
                  <Text
                    fontSize="10px"
                    fontWeight="600"
                    color="#94A3B8"
                    textTransform="uppercase"
                    letterSpacing="0.06em"
                  >
                    Saldo do extrato
                  </Text>
                  {extratoSaldo.continuo != null && (
                    <Badge
                      colorScheme={extratoSaldo.continuo ? 'green' : 'orange'}
                      variant="subtle"
                      fontSize="10px"
                    >
                      {extratoSaldo.continuo ? 'contínuo' : 'quebra de saldo'}
                    </Badge>
                  )}
                </Flex>
                <SimpleGrid columns={2} spacing={3}>
                  <Campo label="Saldo inicial">
                    {extratoSaldo.saldo_inicial != null
                      ? fmtValor(extratoSaldo.saldo_inicial)
                      : '—'}
                  </Campo>
                  <Campo label="Saldo final">
                    {extratoSaldo.saldo_final != null ? fmtValor(extratoSaldo.saldo_final) : '—'}
                  </Campo>
                </SimpleGrid>
                {extratoSaldo.anterior_saldo_final != null && (
                  <Text fontSize="11px" color={corContinuidade} fontWeight="600" mt={2}>
                    Mês anterior fechou em {fmtValor(extratoSaldo.anterior_saldo_final)}
                    {extratoSaldo.continuo === false && extratoSaldo.saldo_inicial != null
                      ? ` · diferença ${fmtValor(
                          Math.abs(extratoSaldo.anterior_saldo_final - extratoSaldo.saldo_inicial),
                        )}`
                      : ''}
                    {extratoSaldo.continuo == null && extratoSaldo.gap_dias != null
                      ? ` · continuidade não avaliável (gap de ${extratoSaldo.gap_dias}d)`
                      : ''}
                  </Text>
                )}
                {extratoSaldo.balanco_ok === false && (
                  <Text fontSize="11px" color="#EF4444" fontWeight="600" mt={1}>
                    Balanço do extrato não fecha
                    {extratoSaldo.divergencia_balanco != null
                      ? ` (divergência ${fmtValor(extratoSaldo.divergencia_balanco)})`
                      : ''}
                  </Text>
                )}
              </Box>
            )}
          </ModalBody>

          <ModalFooter gap={2} borderTopWidth="1px" borderColor="rgba(15, 23, 42, 0.06)">
            <Button
              size="sm"
              leftIcon={<FilePdf size={16} weight="bold" />}
              variant="outline"
              borderRadius="lg"
              onClick={() => setPdfOpen(true)}
            >
              Ver PDF
            </Button>
            <Button
              size="sm"
              leftIcon={<Receipt size={16} weight="bold" />}
              variant="outline"
              borderRadius="lg"
              isDisabled={extratoSaldo?.extrato_id == null}
              onClick={() => setVerExtratoOpen(true)}
            >
              Ver extrato
            </Button>
            <Box flex={1} />
            <Button size="sm" variant="ghost" borderRadius="lg" onClick={onClose}>
              Fechar
            </Button>
            <Tooltip
              label={podeConciliar ? '' : 'Escolha a contrapartida (POC) para conciliar'}
              isDisabled={podeConciliar}
              hasArrow
              shouldWrapChildren
            >
              <Button
                size="sm"
                leftIcon={<Check size={16} weight="bold" />}
                borderRadius="lg"
                color="white"
                fontWeight="600"
                bgGradient="linear(135deg, #6366F1, #1B88F5)"
                _hover={{ filter: 'brightness(1.05)' }}
                isLoading={isPending}
                isDisabled={!podeConciliar || !!lanc?.extrato_bloqueado}
                onClick={concluir}
              >
                Concluir
              </Button>
            </Tooltip>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ExtratoPdfModal
        lancamentoId={pdfOpen ? (lanc?.id ?? null) : null}
        isOpen={pdfOpen}
        onClose={() => setPdfOpen(false)}
      />
      <ExtratoDetailModal
        extrato={extratoDetalhe ?? null}
        isOpen={verExtratoOpen}
        onClose={() => setVerExtratoOpen(false)}
        onBaixar={onBaixarExtrato}
        onReprocessar={onReprocessarExtrato}
        onExcluir={onExcluirExtrato}
        reprocessando={reprocessarExtrato.isPending}
      />
    </>
  );
}
